import { createApp } from 'vue';
import CategoryFacets from './CategoryFacets.vue';
import SearchBar from './SearchBar.vue';
import type { QueryResponse } from './api';
import { initRecommendations } from './recommendations';
// Default-styling følger plugin-en (Vite emitterer dist/bits-search.css) slik at
// søk/fasetter fungerer uten theme-støtte. Butikk-temaer re-skinner ved å sette
// --bits-accent-rgb og/eller overstyre klassene — se styles.scss.
import './styles.scss';

function intOrNull(value: string | null | undefined): number | null {
    if (!value) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function readHydration(el: HTMLElement): { state: QueryResponse | null; cardsHtml: string } {
    const stateEl = el.querySelector<HTMLScriptElement>('script[data-bits-search-state]');
    let state: QueryResponse | null = null;
    if (stateEl?.textContent) {
        try {
            state = JSON.parse(stateEl.textContent) as QueryResponse;
        } catch (e) {
            console.warn('BitsSearch: failed to parse hydration state', e);
        }
    }

    const cardsEl = el.querySelector<HTMLElement>('[data-bits-cards]');
    const cardsHtml = cardsEl ? cardsEl.innerHTML : '';

    return { state, cardsHtml };
}

function mountFacets(el: HTMLElement): void {
    const hydration = readHydration(el);

    const app = createApp(CategoryFacets, {
        categoryId: intOrNull(el.dataset.categoryId),
        manufacturerId: intOrNull(el.dataset.manufacturerId),
        vendorId: intOrNull(el.dataset.vendorId),
        initialText: el.dataset.initialText ?? '',
        isSearchPage: el.dataset.isSearchPage === '1',
        pageSize: intOrNull(el.dataset.pageSize) ?? 24,
        pagingMode: el.dataset.pagingMode === 'pager' ? 'pager' : 'loadmore',
        discountSort: el.dataset.discountSort === '1',
        defaultSort: el.dataset.defaultSort || 'Relevance',
        initialResponse: hydration.state,
        initialCardsHtml: hydration.cardsHtml
    });
    app.mount(el);
}

function mountSearchBar(el: HTMLElement): void {
    const app = createApp(SearchBar, {
        initialQuery: el.dataset.initialQuery ?? '',
        layout: el.dataset.layout === 'fullwidth' ? 'fullwidth' : 'compact',
        contentHeading: el.dataset.contentHeading || 'Innholdssider',
        lipscore: el.dataset.lipscore === '1'
    });
    app.mount(el);
}

function mountAll(): void {
    document.querySelectorAll<HTMLElement>('[data-bits-search="facets"]').forEach(mountFacets);
    document.querySelectorAll<HTMLElement>('[data-bits-search="bar"]').forEach(el => {
        const form = el.querySelector('form');
        if (form) form.remove();
        mountSearchBar(el);
    });
    initRecommendations();
}

// Viewene emitterer script-taggen rått (utenom nops tag-helpers, som ellers
// dedupliserte) — både søkebaren og fasettene kan dermed laste samme fil.
// Kjør bare én gang, ellers monteres appene dobbelt og mount nr. 2 river
// ned DOM-en til den første (nextSibling-krasj i Vue-patcheren).
const bitsWindow = window as unknown as { __bitsSearchLoaded?: boolean };
if (!bitsWindow.__bitsSearchLoaded) {
    bitsWindow.__bitsSearchLoaded = true;
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mountAll, { once: true });
    } else {
        mountAll();
    }
}
